import { Injectable } from '@angular/core';

import { SnCamera, SnCameraStatus } from '@lib/api/entities';

const CLOUD_URL_PARSE_ERROR_MSG = 'Failed to parse cloud URL';
const LOCAL_URL_PARSE_ERROR_MSG = 'Failed to parse local URL';

export enum ConnectionType {
  Cloud = 'cloud',
  Local = 'local',
  Unknown = 'unknown',
}

export interface CloudStreamData {
  nimbleCloudServerId?: string;
  applianceId?: string;
  appName?: string;
  tunnelPort?: number;
}

export interface LocalStreamData {
  coreSerialNumber?: string;
  appName?: string;
  tokenExpiration?: number;
}

export interface StreamContext {
  connectionType?: ConnectionType;
  organizationId?: string;
  streamType?: string;
  cloudData?: CloudStreamData;
  localData?: LocalStreamData;
  coreSerialNumber?: string;
  nimbleCloudServerId?: string;
  appName?: string;
  applianceId?: string;
  tokenExpiration?: number;
  tunnelPort?: number;
  playback_method?: 'manifest' | 'seek';
  protocol?: string;
  source?: 'cloud' | 'local';
  camera_id?: string;
  device_id?: string;
  source_ui?: string;
  camera_status?: SnCameraStatus;
  live?: boolean;
}

@Injectable({ providedIn: 'root' })
export class StreamAnalysisService {
  public url: string = '';
  public nimbleCloudServerId: string = '';
  public applianceId: string = '';
  public appName: string = '';
  public tokenExpiration: number;
  public tunnelPort: number;
  public coreSerialNumber: string;

  constructor() {}

  public getStreamContextStart(camera: SnCamera): StreamContext {
    if (!camera) {
      return {
        organizationId: undefined,
        streamType: ConnectionType.Unknown,
      };
    }
    const liveVideoCamera = camera.liveVideoUrls?.main;
    const cloudUrl = liveVideoCamera?.cloud?.sldp;
    const localUrl = liveVideoCamera?.local?.sldp;
    const playbackUrl = camera.playbackUrls?.main?.cloud?.video;

    const cloudData = this.breakdownCloudUrl(cloudUrl, playbackUrl);
    const localData = this.breakdownLocalUrl(localUrl);

    if (!cloudData && !localData) {
      return {
        organizationId: camera.organization?.id,
        streamType: ConnectionType.Unknown,
        camera_status: camera.status,
      };
    }

    return {
      organizationId: camera.organization?.id,
      cloudData,
      localData,
      camera_status: camera.status,
    };
  }

  public getStreamContextEnd(camera: SnCamera, isCloud: boolean): Partial<StreamContext> {
    if (!camera) {
      return {
        organizationId: undefined,
        streamType: ConnectionType.Unknown,
      };
    }
    const liveVideoCamera = camera.liveVideoUrls?.main;
    const url = isCloud ? liveVideoCamera?.cloud?.sldp : liveVideoCamera?.local?.sldp;

    const playbackUrl = camera.playbackUrls?.main?.cloud?.video;

    const data = isCloud ? this.breakdownCloudUrl(url, playbackUrl) : this.breakdownLocalUrl(url);
    const streamType = isCloud ? ConnectionType.Cloud : ConnectionType.Local;

    return {
      organizationId: camera.organization?.id,
      streamType,
      ...(isCloud ? { cloudData: data } : { localData: data }),
      camera_status: camera.status,
    };
  }

  public breakdownCloudUrl(
    cloudUrl: string | undefined,
    playbackUrl?: string | undefined,
  ): CloudStreamData | undefined {
    if (!cloudUrl || !playbackUrl) {
      return undefined;
    }
    try {
      const cloudUrlObj = new URL(cloudUrl);
      const playbackUrlObj = new URL(playbackUrl);

      const hostname = cloudUrlObj.hostname.split('.')[0] || '';
      const nimbleCloudServerId = this.extractHostPrefix(hostname, 3);

      const pathSegments = this.getCleanPathSegments(cloudUrlObj.pathname);
      const applianceSegment = pathSegments[0] || '';
      const applianceParts = applianceSegment.split('_');
      const applianceId = applianceParts[0] || '';
      const appName = applianceParts.slice(2).join('_') || '';

      const playbackSegments = this.getCleanPathSegments(playbackUrlObj.pathname);
      const tunnelPortSegment = this.findNumericSegment([playbackSegments[2]]);
      const tunnelPort = tunnelPortSegment ? this.parseValidNumber(tunnelPortSegment) : undefined;

      return {
        nimbleCloudServerId,
        applianceId,
        appName,
        tunnelPort,
      };
    } catch (error) {
      console.error(CLOUD_URL_PARSE_ERROR_MSG, cloudUrl, playbackUrl);
      return undefined;
    }
  }

  public breakdownLocalUrl(localUrl: string | undefined): LocalStreamData | undefined {
    if (!localUrl) {
      return undefined;
    }
    try {
      const localUrlObj = new URL(localUrl);
      const coreSerialNumber = localUrlObj.hostname.split('.')[0] || '';

      const pathSegments = this.getCleanPathSegments(localUrlObj.pathname);
      const tokenExpirationSegment = this.findNumericSegment(pathSegments);
      const tokenExpiration = tokenExpirationSegment
        ? this.parseValidNumber(tokenExpirationSegment)
        : undefined;

      const appNameSegment = this.findUnderscoreSegment(pathSegments);
      const appName = appNameSegment ? appNameSegment.split('_').slice(1).join('_') || '' : '';

      return {
        coreSerialNumber,
        appName,
        tokenExpiration,
      };
    } catch (error) {
      console.error(LOCAL_URL_PARSE_ERROR_MSG, localUrl);
      return undefined;
    }
  }

  public getCleanPathSegments(pathname: string): string[] {
    return pathname.split('/').filter((segment) => segment.length > 0);
  }

  public findNumericSegment(segments: string[]): string | undefined {
    return segments.find((segment) => /^\d+$/.test(segment));
  }

  public findUnderscoreSegment(segments: string[]): string | undefined {
    return segments.find((segment) => segment.includes('_'));
  }

  public parseValidNumber(value: string): number | undefined {
    if (value === '') {
      return undefined;
    }
    const num = Number(value);
    return !isNaN(num) ? num : undefined;
  }

  public extractHostPrefix(hostname: string, prefixParts: number): string {
    const parts = hostname.split('-');
    return parts.slice(0, prefixParts).join('-') || hostname;
  }
}
